import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { _isNil } from '@/littledash';
import cn from 'classnames';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import styles from '../DataTableV2.module.scss';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from '../hooks/useCellCoordinate';
import { useCellData } from '../hooks/useCellData';
import { useCellPositioning } from '../hooks/useCellPositioning';
import { useCellSelectionState } from '../hooks/useCellSelectionState';
import { useCellState } from '../hooks/useCellState';

interface DataTableTimestampCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

/**
 * Pure display cell for timestamp / timestampBaseline columns.
 *
 * Renders the datetime value (or "Add Timestamp" placeholder when empty) with no
 * editing affordances. Editing is handled by the centralized SingleCellEditor via
 * DateCellEditor.
 */
export const DataTableTimestampCell: FC<DataTableTimestampCellProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { containerStyle } = useCellPositioning(positionOffset);

  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: hasGroupBorder })}
      style={containerStyle}
      data-cell-coordinate={`${coordinate.column},${coordinate.row}`}
    >
      {cellState === 'Loading' ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={tooltip}>
          <div className={cn(styles.Cell, styles[selectionState], styles[cellState])} data-state={cellState}>
            <span className={styles.TimestampValue}>
              {_isNil(cellData?.value) ? (
                <>Add Timestamp</>
              ) : (
                <DateTimeRenderer value={cellData.value} defaultResponse={cellData.value} />
              )}
            </span>
          </div>
        </Tooltip>
      )}
    </div>
  );
};
